import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMemo, useState } from 'react';

/**
 * The properties for the video skipped dialog component
 */
interface VideoSkippedDialogProps {
  /**
   * The current state of the dialog
   */
  open: boolean;
  /**
   * Set the current state of the dialog
   * @param _ The state of the dialog
   */
  setOpen: (_: boolean) => void;
  /**
   * The current progress for the user through the video
   */
  videoTracking: boolean[];
  /**
   * The total video duration
   */
  duration: number;
}

/**
 * The video skipped dialog to show when we have detected that the user has skipped
 * part of the video.
 */
const VideoSkippedDialog = (props: VideoSkippedDialogProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  /**
   * Returns an array of strings representing unwatched segments of a video.
   * Each item in the array has the format "m:ss-m:ss".
   */
  const unwatchedSegments = useMemo(() => {
    // If video duration is 0, return an empty array.
    if (props.duration === 0) {
      return [];
    }

    // Helper to format seconds as m:ss
    const formatTime = (seconds: number): string => {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const unwatchedRanges: string[] = [];

    // Tracks the start index of a consecutive unwatched (false) block
    let startIndex: number | null = null;

    for (let i = 0; i < props.videoTracking.length; i++) {
      if (!props.videoTracking[i]) {
        // If we find an unwatched segment and we're not already tracking a start, mark it
        if (startIndex === null) {
          startIndex = i;
        }
      } else {
        // If we encounter a watched segment but had been tracking an unwatched block, close that range
        if (startIndex !== null) {
          const startTime = (startIndex / 100) * props.duration;
          const endTime = (i / 100) * props.duration;
          unwatchedRanges.push(
            `${formatTime(startTime)}-${formatTime(endTime)}`
          );
          startIndex = null;
        }
      }
    }

    // If the array ends with an unwatched segment, close the last range
    if (startIndex !== null) {
      const startTime = (startIndex / 100) * props.duration;
      const endTime = props.duration;
      unwatchedRanges.push(`${formatTime(startTime)}-${formatTime(endTime)}`);
    }

    return unwatchedRanges;
  }, [props.duration, props.videoTracking]);

  return (
    <Dialog
      open={props.open}
      onClose={() => props.setOpen(false)}
      fullScreen={fullScreen}
      fullWidth={true}
      maxWidth="sm"
    >
      <DialogTitle>Detected Video Skip</DialogTitle>
      <DialogContent>
        We have detected that you have skipped parts of the video. Please watch
        the video in its entirety. If the video is not completely viewed, you
        will not be able to proceed to the next module.
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            mt: 3,
            mb: 1,
          }}
          onClick={() => setDetailsExpanded((d) => !d)}
        >
          <Typography color="textSecondary">
            {detailsExpanded ? 'Hide' : 'Expand'} Details
          </Typography>
          <ExpandMoreRoundedIcon
            sx={{
              color: (t) => t.palette.text.secondary,
              transform: detailsExpanded ? 'rotate(180deg)' : 'initial',
            }}
          />
        </Box>
        {detailsExpanded &&
          (unwatchedSegments ? (
            <Card sx={{ padding: 1 }}>
              <Typography sx={{ fontWeight: 'bold' }}>
                Unwatched Segments
              </Typography>
              {unwatchedSegments.map((s) => (
                <Typography key={s}>{s}</Typography>
              ))}
            </Card>
          ) : (
            'We were unable to detect the total video length to show more details. If this continues, please contact support.'
          ))}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => props.setOpen(false)}
          variant="contained"
          color="primary"
          autoFocus
        >
          Acknowledge
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoSkippedDialog;
